/**
 * Phase 9-B · PR-4B · Contractor-facing Change Order workspace
 * ===============================================================
 *
 * Reuses the existing PR-2 REST surface (`/api/change-orders`) — no
 * parallel frontend state model, no duplicated backend calculations.
 * All lifecycle mutations (approve/cancel) stage a Phase 5C
 * confirmation first, then re-submit with the returned nonce.
 *
 * PHASE 10 code fence (repeated for future readers):
 *   * NO customer approval / signature / e-sign / delivery UI here.
 *   * NO public URL, no invoice creation, no schedule mutation.
 *   * The word "Approve draft" and "Contractor approval" are used
 *     deliberately — never "Customer approval".
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, money, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import { Mark } from "@/components/Logo";
import {
  ArrowLeft, CheckCircle2, XCircle, Printer, PenLine, Eye,
  Plus, Loader2, ClipboardList, ChevronRight, ShieldCheck, Clock,
  RefreshCw,
} from "lucide-react";

// ---------------------------------------------------------------- copy

/**
 * Inline bilingual copy for the change-order surface only. Keeping
 * these localized strings scoped to this file (rather than editing
 * the giant `onboarding_i18n.js` dictionary) keeps the PR bounded
 * per the PO's "no visual modernization outside change-order surface"
 * constraint. English is authoritative; Spanish is a translator-
 * ready mirror.
 */
const COPY = {
  en: {
    workspace_title: "Change orders",
    workspace_subtitle: "Contractor-only workspace for this project's change orders.",
    empty_title: "No change orders yet",
    empty_body: "When you or Levi drafts a change order for this project, it will appear here.",
    new_btn: "New draft",
    back_to_project: "Back to project",
    list_col_number: "#",
    list_col_summary: "Scope summary",
    list_col_net: "Net change",
    list_col_status: "Status",
    list_col_updated: "Updated",
    status_draft: "Draft",
    status_contractor_approved: "Contractor-approved",
    status_canceled: "Canceled",
    status_superseded: "Superseded",
    status_locked_hint: "Immutable",
    section_scope: "Scope changes",
    section_financial: "Financial breakdown",
    section_schedule: "Schedule impact",
    section_history: "Contractor activity",
    section_source: "Source contract",
    additions: "Additions",
    deductions: "Deductions",
    substitutions: "Substitutions",
    substitution_before: "Was",
    substitution_after: "Now",
    credits: "Customer credits",
    zero_scope_only: "Scope-only change · no dollar impact",
    zero_schedule_only: "Schedule-only change · no dollar impact",
    zero_no_impact: "No dollar impact",
    additions_subtotal: "Additions subtotal",
    deductions_subtotal: "Deductions subtotal",
    modifications_subtotal: "Substitutions net",
    tax_delta: "Tax adjustment",
    net_price_delta: "Net change (pre-tax)",
    total_delta: "Net change to contract",
    original_contract_value: "Original contract value",
    revised_contract_value: "Revised contract value",
    schedule_days_added: "Added days",
    schedule_days_removed: "Days recovered",
    schedule_no_impact: "No schedule impact",
    schedule_target: "New target completion",
    approve_btn: "Approve draft",
    approve_confirm_title: "Ready to approve this draft?",
    approve_confirm_body: "This finalizes and freezes the draft for internal record. It does NOT send anything to the customer or trigger a signature.",
    approve_confirm_yes: "Yes, approve as contractor",
    approve_confirm_cancel: "Not yet",
    approve_success: "Draft contractor-approved.",
    cancel_btn: "Cancel draft",
    cancel_confirm_title: "Cancel this draft?",
    cancel_reason_placeholder: "Optional reason (visible only to your team)",
    cancel_confirm_yes: "Yes, cancel",
    cancel_confirm_no: "Never mind",
    cancel_success: "Draft canceled.",
    edit_btn: "Edit draft",
    edit_save: "Save edits",
    edit_cancel: "Discard changes",
    edit_scope_en: "Scope summary (English)",
    edit_scope_es: "Scope summary (Spanish)",
    edit_schedule_days: "Schedule impact in days (positive adds, negative recovers)",
    edit_schedule_notes_en: "Schedule notes (English)",
    edit_schedule_notes_es: "Schedule notes (Spanish)",
    edit_locked: "This change order is locked and cannot be edited.",
    printable_preview_btn: "Printable preview",
    preview_back: "Back",
    preview_print: "Print",
    preview_draft_watermark: "Draft · internal review only",
    preview_kind_label: "Change order",
    preview_number: "Number",
    preview_status: "Status",
    preview_customer: "Customer",
    preview_project: "Project",
    preview_source_contract: "Source contract",
    preview_signed_hash: "Signed content hash",
    preview_scope_summary: "Scope summary",
    preview_no_scope: "No line-item detail on this change order.",
    fallback_error: "Something went wrong. Please try again.",
    action_type_created: "Created",
    action_type_approved: "Contractor-approved",
    action_type_canceled: "Canceled",
    action_type_superseded: "Superseded",
    upf_note: "This change order is part of the project's unified record.",
    contractor_approval_note: "Contractor approval only — no customer notice is sent, no signature is captured, no invoice is created.",
  },
  es: {
    workspace_title: "Órdenes de cambio",
    workspace_subtitle: "Espacio interno del contratista para las órdenes de cambio de este proyecto.",
    empty_title: "Aún no hay órdenes de cambio",
    empty_body: "Cuando usted o Levi redacten una orden de cambio para este proyecto, aparecerá aquí.",
    new_btn: "Nuevo borrador",
    back_to_project: "Volver al proyecto",
    list_col_number: "#",
    list_col_summary: "Resumen del alcance",
    list_col_net: "Cambio neto",
    list_col_status: "Estado",
    list_col_updated: "Actualizado",
    status_draft: "Borrador",
    status_contractor_approved: "Aprobado por el contratista",
    status_canceled: "Cancelado",
    status_superseded: "Reemplazado",
    status_locked_hint: "Inmutable",
    section_scope: "Cambios de alcance",
    section_financial: "Desglose financiero",
    section_schedule: "Impacto al cronograma",
    section_history: "Actividad del contratista",
    section_source: "Contrato de origen",
    additions: "Adiciones",
    deductions: "Deducciones",
    substitutions: "Sustituciones",
    substitution_before: "Antes",
    substitution_after: "Ahora",
    credits: "Créditos al cliente",
    zero_scope_only: "Cambio de alcance sin impacto económico",
    zero_schedule_only: "Cambio de cronograma sin impacto económico",
    zero_no_impact: "Sin impacto económico",
    additions_subtotal: "Subtotal de adiciones",
    deductions_subtotal: "Subtotal de deducciones",
    modifications_subtotal: "Neto de sustituciones",
    tax_delta: "Ajuste de impuestos",
    net_price_delta: "Cambio neto (antes de impuestos)",
    total_delta: "Cambio neto al contrato",
    original_contract_value: "Valor original del contrato",
    revised_contract_value: "Valor revisado del contrato",
    schedule_days_added: "Días añadidos",
    schedule_days_removed: "Días recuperados",
    schedule_no_impact: "Sin impacto en el cronograma",
    schedule_target: "Nueva fecha de finalización objetivo",
    approve_btn: "Aprobar borrador",
    approve_confirm_title: "¿Listo para aprobar este borrador?",
    approve_confirm_body: "Esto finaliza y congela el borrador para su registro interno. NO envía nada al cliente ni activa ninguna firma.",
    approve_confirm_yes: "Sí, aprobar como contratista",
    approve_confirm_cancel: "Todavía no",
    approve_success: "Borrador aprobado por el contratista.",
    cancel_btn: "Cancelar borrador",
    cancel_confirm_title: "¿Cancelar este borrador?",
    cancel_reason_placeholder: "Razón opcional (visible solo para su equipo)",
    cancel_confirm_yes: "Sí, cancelar",
    cancel_confirm_no: "No, mejor no",
    cancel_success: "Borrador cancelado.",
    edit_btn: "Editar borrador",
    edit_save: "Guardar cambios",
    edit_cancel: "Descartar cambios",
    edit_scope_en: "Resumen del alcance (Inglés)",
    edit_scope_es: "Resumen del alcance (Español)",
    edit_schedule_days: "Impacto de cronograma en días (positivo suma, negativo recupera)",
    edit_schedule_notes_en: "Notas de cronograma (Inglés)",
    edit_schedule_notes_es: "Notas de cronograma (Español)",
    edit_locked: "Esta orden de cambio está bloqueada y no se puede editar.",
    printable_preview_btn: "Vista imprimible",
    preview_back: "Volver",
    preview_print: "Imprimir",
    preview_draft_watermark: "Borrador · solo revisión interna",
    preview_kind_label: "Orden de cambio",
    preview_number: "Número",
    preview_status: "Estado",
    preview_customer: "Cliente",
    preview_project: "Proyecto",
    preview_source_contract: "Contrato de origen",
    preview_signed_hash: "Hash del contenido firmado",
    preview_scope_summary: "Resumen del alcance",
    preview_no_scope: "Sin detalle de partidas en esta orden de cambio.",
    fallback_error: "Algo salió mal. Intente nuevamente.",
    action_type_created: "Creado",
    action_type_approved: "Aprobado por el contratista",
    action_type_canceled: "Cancelado",
    action_type_superseded: "Reemplazado",
    upf_note: "Esta orden de cambio forma parte del expediente unificado del proyecto.",
    contractor_approval_note: "Solo aprobación del contratista — no se envía aviso al cliente, no se captura firma, no se crea factura.",
  },
};

const useCopy = (forceLang) => {
  const { lang } = useLang();
  const raw = forceLang || (lang === "es" ? "es" : "en");
  const l = raw === "es" ? "es" : "en";
  return { lang: l, c: COPY[l] };
};

// Presentation helpers -------------------------------------------------------

const StatusPill = ({ status }) => {
  const { c } = useCopy();
  const label = ({
    draft: c.status_draft,
    contractor_approved: c.status_contractor_approved,
    canceled: c.status_canceled,
    superseded: c.status_superseded,
  })[status] || status;
  const tone = ({
    draft: "bg-amber-100 text-amber-900",
    contractor_approved: "bg-emerald-100 text-emerald-900",
    canceled: "bg-slate-200 text-slate-700",
    superseded: "bg-slate-200 text-slate-700",
  })[status] || "bg-slate-200 text-slate-700";
  return (
    <span
      data-testid={`co-status-${status}`}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold ${tone}`}
    >
      {status === "contractor_approved" && <ShieldCheck size={12} />}
      {status === "draft" && <PenLine size={12} />}
      {label}
    </span>
  );
};

// ---------------------------------------------------------------- LIST

export const ChangeOrdersList = () => {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { lang, c } = useCopy();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useLeviWorkspaceHint("change_orders_list", {
    active_project_id: pid,
    current_section: "change_orders_list",
  });

  useEffect(() => {
    let cancel = false;
    api.get(`/change-orders?project_id=${encodeURIComponent(pid)}`)
      .then((r) => {
        if (cancel) return;
        setItems(r.data.change_orders || []);
      })
      .catch((e) => {
        if (cancel) return;
        setError(e?.response?.data?.detail?.message?.[lang]
                 || e?.response?.data?.detail?.message
                 || c.fallback_error);
        setItems([]);
      });
    return () => { cancel = true; };
  }, [pid, lang, c.fallback_error]);

  return (
    <DashboardLayout
      title={c.workspace_title}
      subtitle={c.workspace_subtitle}
      actions={
        <Link
          to={`/app/projects/${pid}`}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          data-testid="co-back-to-project"
        >
          <ArrowLeft size={14} /> {c.back_to_project}
        </Link>
      }
    >
      <div data-testid="change-orders-workspace" className="space-y-4">
        {error && (
          <div className="jc-card p-4 border-l-4 border-red-500 text-sm" data-testid="co-list-error">
            {error}
          </div>
        )}
        {items === null ? (
          <div className="jc-card p-8 text-center text-sm text-[var(--jc-text-soft)]">
            <Loader2 className="inline animate-spin" size={16} /> ...
          </div>
        ) : items.length === 0 ? (
          <div className="jc-card p-12 text-center" data-testid="co-list-empty">
            <ClipboardList className="mx-auto mb-3 text-[var(--jc-text-soft)]" size={28} />
            <div className="font-display text-xl">{c.empty_title}</div>
            <p className="text-sm text-[var(--jc-text-soft)] mt-2">{c.empty_body}</p>
          </div>
        ) : (
          <div className="jc-card overflow-hidden" data-testid="co-list-table">
            <table className="w-full text-sm">
              <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">{c.list_col_number}</th>
                  <th className="text-left px-5 py-3 font-semibold">{c.list_col_summary}</th>
                  <th className="text-right px-5 py-3 font-semibold">{c.list_col_net}</th>
                  <th className="text-left px-5 py-3 font-semibold">{c.list_col_status}</th>
                  <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{c.list_col_updated}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--jc-border)]">
                {items.map((co) => {
                  const summary = lang === "es"
                    ? (co.scope_delta?.scope_summary_es || co.scope_delta?.scope_summary_en || "—")
                    : (co.scope_delta?.scope_summary_en || co.scope_delta?.scope_summary_es || "—");
                  const td = co.financial_delta?.total_delta ?? 0;
                  return (
                    <tr
                      key={co.id}
                      className="hover:bg-[var(--jc-bg)] cursor-pointer"
                      onClick={() => navigate(`/app/projects/${pid}/change-orders/${co.id}`)}
                      data-testid={`co-row-${co.id}`}
                    >
                      <td className="px-5 py-3 font-semibold" data-testid={`co-row-number-${co.id}`}>#{co.change_order_number}</td>
                      <td className="px-5 py-3">{summary}</td>
                      <td className="px-5 py-3 text-right font-mono-jc" data-testid={`co-row-total-${co.id}`}>{money(td)}</td>
                      <td className="px-5 py-3"><StatusPill status={co.status} /></td>
                      <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{formatDate(co.updated_at, lang)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------- DETAIL

const FinancialRow = ({ label, amount, testid, tone = "" }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className={tone === "muted" ? "text-[var(--jc-text-soft)]" : ""}>{label}</span>
    <span
      className={`font-mono-jc ${tone === "bold" ? "font-semibold" : ""}`}
      data-testid={testid}
    >
      {money(amount)}
    </span>
  </div>
);

const ScopeSection = ({ co, forceLang }) => {
  const { lang, c } = useCopy(forceLang);
  const sd = co.scope_delta || {};
  const added = sd.added_line_items || [];
  const removed = sd.removed_line_items || [];
  const modified = sd.modified_line_items || [];
  const nonCreditAdded = added.filter((li) => !li.is_credit);
  const creditAdded = added.filter((li) => li.is_credit);
  const summary = (lang === "es"
    ? (sd.scope_summary_es || sd.scope_summary_en)
    : (sd.scope_summary_en || sd.scope_summary_es)) || "";

  const nothing = !nonCreditAdded.length && !removed.length
    && !modified.length && !creditAdded.length;
  const totalDelta = co.financial_delta?.total_delta ?? 0;
  const scheduleImpact = co.schedule_delta
    && (co.schedule_delta.days_added !== 0
        || (co.schedule_delta.notes_en || co.schedule_delta.notes_es));

  return (
    <section className="jc-card p-6" data-testid="co-section-scope">
      <div className="overline mb-3">{c.section_scope}</div>
      {summary && (
        <p className="text-sm mb-4" data-testid="co-scope-summary">{summary}</p>
      )}
      {nothing && totalDelta === 0 && (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="co-scope-zero">
          {scheduleImpact ? c.zero_schedule_only : c.zero_scope_only}
        </div>
      )}
      {nonCreditAdded.length > 0 && (
        <div className="mb-4" data-testid="co-scope-additions">
          <div className="text-xs font-semibold text-emerald-800 mb-2">{c.additions}</div>
          <ul className="space-y-1 text-sm">
            {nonCreditAdded.map((li, i) => (
              <li key={i} className="flex justify-between" data-testid={`co-added-${i}`}>
                <span>{li.description} {li.quantity !== 1 ? `· ${li.quantity} ${li.unit}` : ""}</span>
                <span className="font-mono-jc">{money(li.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {creditAdded.length > 0 && (
        <div className="mb-4" data-testid="co-scope-credits">
          <div className="text-xs font-semibold text-blue-800 mb-2">{c.credits}</div>
          <ul className="space-y-1 text-sm">
            {creditAdded.map((li, i) => (
              <li key={i} className="flex justify-between" data-testid={`co-credit-${i}`}>
                <span>{li.description}</span>
                <span className="font-mono-jc">−{money(Math.abs(li.total))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {removed.length > 0 && (
        <div className="mb-4" data-testid="co-scope-deductions">
          <div className="text-xs font-semibold text-rose-800 mb-2">{c.deductions}</div>
          <ul className="space-y-1 text-sm">
            {removed.map((li, i) => {
              const qty = li.removed_quantity ?? li.source_quantity;
              const amt = (qty || 0) * (li.source_unit_price || 0);
              return (
                <li key={i} className="flex justify-between" data-testid={`co-removed-${i}`}>
                  <span>{li.source_description} {qty !== li.source_quantity ? `· ${qty}/${li.source_quantity} ${c.deductions.toLowerCase()}` : ""}</span>
                  <span className="font-mono-jc">−{money(amt)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {modified.length > 0 && (
        <div className="mb-2" data-testid="co-scope-substitutions">
          <div className="text-xs font-semibold text-indigo-800 mb-2">{c.substitutions}</div>
          <ul className="space-y-2 text-sm">
            {modified.map((li, i) => {
              const net = (li.after?.total || 0) - (li.before?.source_total || 0);
              return (
                <li key={i} className="rounded-md border border-[var(--jc-border)] p-2" data-testid={`co-modified-${i}`}>
                  <div className="text-[var(--jc-text-soft)] text-xs">{c.substitution_before}:</div>
                  <div className="flex justify-between"><span>{li.before?.source_description}</span><span className="font-mono-jc">{money(li.before?.source_total)}</span></div>
                  <div className="text-[var(--jc-text-soft)] text-xs mt-1">{c.substitution_after}:</div>
                  <div className="flex justify-between"><span>{li.after?.description}</span><span className="font-mono-jc">{money(li.after?.total)}</span></div>
                  <div className="text-xs text-right mt-1 font-semibold" data-testid={`co-modified-net-${i}`}>{net >= 0 ? "+" : "−"}{money(Math.abs(net))}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

const FinancialSection = ({ co, forceLang }) => {
  const { c } = useCopy(forceLang);
  const fd = co.financial_delta || {};
  return (
    <section className="jc-card p-6" data-testid="co-section-financial">
      <div className="overline mb-3">{c.section_financial}</div>
      <FinancialRow label={c.original_contract_value} amount={fd.original_contract_value} testid="co-fin-original" tone="muted" />
      <div className="border-t border-[var(--jc-border)] my-2" />
      <FinancialRow label={c.additions_subtotal} amount={fd.added_subtotal} testid="co-fin-additions" />
      <FinancialRow label={c.deductions_subtotal} amount={fd.removed_subtotal} testid="co-fin-deductions" />
      <FinancialRow label={c.modifications_subtotal} amount={fd.modified_subtotal_delta} testid="co-fin-modifications" />
      <FinancialRow label={c.tax_delta} amount={fd.tax_delta} testid="co-fin-tax" />
      <div className="border-t border-[var(--jc-border)] my-2" />
      <FinancialRow label={c.net_price_delta} amount={fd.net_price_delta} testid="co-fin-net-pretax" />
      <FinancialRow label={c.total_delta} amount={fd.total_delta} testid="co-fin-total" tone="bold" />
      <div className="border-t border-[var(--jc-border)] my-2" />
      <FinancialRow label={c.revised_contract_value} amount={fd.revised_contract_value} testid="co-fin-revised" tone="bold" />
    </section>
  );
};

const ScheduleSection = ({ co, forceLang }) => {
  const { lang, c } = useCopy(forceLang);
  const sd = co.schedule_delta;
  const notes = sd && (lang === "es" ? (sd.notes_es || sd.notes_en) : (sd.notes_en || sd.notes_es));
  return (
    <section className="jc-card p-6" data-testid="co-section-schedule">
      <div className="overline mb-3">{c.section_schedule}</div>
      {!sd || (sd.days_added === 0 && !sd.new_target_completion_date && !notes) ? (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="co-schedule-none">
          <Clock className="inline mr-1" size={14} /> {c.schedule_no_impact}
        </div>
      ) : (
        <div className="text-sm space-y-2">
          {sd.days_added !== 0 && (
            <div className="flex justify-between" data-testid="co-schedule-days">
              <span>{sd.days_added > 0 ? c.schedule_days_added : c.schedule_days_removed}</span>
              <span className="font-mono-jc">{Math.abs(sd.days_added)}</span>
            </div>
          )}
          {sd.new_target_completion_date && (
            <div className="flex justify-between" data-testid="co-schedule-target">
              <span>{c.schedule_target}</span>
              <span>{formatDate(sd.new_target_completion_date, lang)}</span>
            </div>
          )}
          {notes && <p className="text-[var(--jc-text-soft)]" data-testid="co-schedule-notes">{notes}</p>}
        </div>
      )}
    </section>
  );
};

const HistorySection = ({ co }) => {
  const { c } = useCopy();
  const rows = co.history || [];
  const eventLabel = (ev) => ({
    draft_created: c.action_type_created,
    contractor_approved: c.action_type_approved,
    canceled: c.action_type_canceled,
    superseded: c.action_type_superseded,
    edited: c.edit_btn,
  })[ev] || ev;
  return (
    <section className="jc-card p-6" data-testid="co-section-history">
      <div className="overline mb-3">{c.section_history}</div>
      <ul className="text-sm space-y-2">
        {rows.map((h, i) => (
          <li key={i} className="flex justify-between items-center" data-testid={`co-history-${i}`}>
            <span>{eventLabel(h.event)}</span>
            <span className="text-xs text-[var(--jc-text-soft)]">{formatDate(h.at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const EditPanel = ({ co, onSaved, onCancel }) => {
  const { c } = useCopy();
  const [scopeEn, setScopeEn] = useState(co.scope_delta?.scope_summary_en || "");
  const [scopeEs, setScopeEs] = useState(co.scope_delta?.scope_summary_es || "");
  const [days, setDays] = useState(String(co.schedule_delta?.days_added ?? 0));
  const [notesEn, setNotesEn] = useState(co.schedule_delta?.notes_en || "");
  const [notesEs, setNotesEs] = useState(co.schedule_delta?.notes_es || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        scope_delta: {
          ...co.scope_delta,
          scope_summary_en: scopeEn,
          scope_summary_es: scopeEs,
        },
      };
      const daysN = parseInt(days, 10);
      if (!Number.isNaN(daysN) || notesEn || notesEs) {
        body.schedule_delta = {
          days_added: Number.isNaN(daysN) ? 0 : daysN,
          new_target_completion_date: co.schedule_delta?.new_target_completion_date || null,
          notes_en: notesEn,
          notes_es: notesEs,
        };
      }
      const { data } = await api.patch(`/change-orders/${co.id}`, body);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message?.en || c.fallback_error);
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="jc-card p-6" data-testid="co-edit-panel">
      <div className="overline mb-3">{c.edit_btn}</div>
      <div className="space-y-3 text-sm">
        <div>
          <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{c.edit_scope_en}</label>
          <textarea data-testid="co-edit-scope-en" value={scopeEn} onChange={(e) => setScopeEn(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{c.edit_scope_es}</label>
          <textarea data-testid="co-edit-scope-es" value={scopeEs} onChange={(e) => setScopeEs(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{c.edit_schedule_days}</label>
          <input data-testid="co-edit-schedule-days" type="number" value={days} onChange={(e) => setDays(e.target.value)} className="w-32 px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{c.edit_schedule_notes_en}</label>
          <textarea data-testid="co-edit-schedule-notes-en" value={notesEn} onChange={(e) => setNotesEn(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{c.edit_schedule_notes_es}</label>
          <textarea data-testid="co-edit-schedule-notes-es" value={notesEs} onChange={(e) => setNotesEs(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm" />
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} data-testid="co-edit-save" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} {c.edit_save}
          </button>
          <button onClick={onCancel} disabled={saving} data-testid="co-edit-cancel" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-[var(--jc-border-strong)]">
            {c.edit_cancel}
          </button>
        </div>
      </div>
    </section>
  );
};

/**
 * Two-step Phase 5C confirmation for approve/cancel. First call
 * without a nonce stages the pending confirmation and returns
 * `{pending_confirmation: True, nonce, revision, expires_at}`.
 * Second call with that nonce executes.
 */
const useTwoStepAction = ({ coid, action, onSuccess, successMessage, fallbackError }) => {
  const [staging, setStaging] = useState(false);
  const [pending, setPending] = useState(null);   // {nonce}
  const [reason, setReason] = useState("");
  const stage = async () => {
    setStaging(true);
    try {
      const body = action === "cancel" && reason ? { cancel_reason: reason } : {};
      const { data } = await api.post(`/change-orders/${coid}/${action}`, body);
      if (data?.pending_confirmation) {
        setPending({ nonce: data.nonce });
      } else {
        onSuccess(data);
        toast.success(successMessage);
        setPending(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message?.en || fallbackError);
    } finally {
      setStaging(false);
    }
  };
  const confirm = async () => {
    if (!pending?.nonce) return;
    setStaging(true);
    try {
      const body = { nonce: pending.nonce };
      if (action === "cancel" && reason) body.cancel_reason = reason;
      const { data } = await api.post(`/change-orders/${coid}/${action}`, body);
      onSuccess(data);
      toast.success(successMessage);
      setPending(null);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message?.en || fallbackError);
    } finally {
      setStaging(false);
    }
  };
  const dismiss = () => { setPending(null); setReason(""); };
  return { stage, confirm, dismiss, staging, pending, reason, setReason };
};

export const ChangeOrderDetail = () => {
  const { pid, coid } = useParams();
  const navigate = useNavigate();
  const { lang, c } = useCopy();
  const [co, setCo] = useState(null);
  const [editing, setEditing] = useState(false);

  useLeviWorkspaceHint("change_order_detail", {
    active_project_id: pid,
    open_document_type: "change_order",
    open_document_id: coid,
    current_section: "change_order_detail",
  });

  const load = () => api.get(`/change-orders/${coid}`).then((r) => setCo(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [coid]);

  const approveFlow = useTwoStepAction({
    coid, action: "approve",
    onSuccess: setCo,
    successMessage: c.approve_success,
    fallbackError: c.fallback_error,
  });
  const cancelFlow = useTwoStepAction({
    coid, action: "cancel",
    onSuccess: setCo,
    successMessage: c.cancel_success,
    fallbackError: c.fallback_error,
  });

  if (!co) return <DashboardLayout title={c.workspace_title} subtitle="…"><div /></DashboardLayout>;

  const isDraft = co.status === "draft";
  const isImmutable = ["contractor_approved", "canceled", "superseded"].includes(co.status);

  return (
    <DashboardLayout
      title={`${c.preview_kind_label} #${co.change_order_number}`}
      subtitle={<StatusPill status={co.status} />}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate(`/app/projects/${pid}/change-orders`)} className="inline-flex items-center gap-1 text-sm text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]" data-testid="co-back-list">
            <ArrowLeft size={14} /> {c.back_to_project}
          </button>
          <Link
            to={`/app/projects/${pid}/change-orders/${coid}/preview`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            data-testid="co-open-preview"
          >
            <Eye size={14} /> {c.printable_preview_btn}
          </Link>
          {isDraft && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"
              data-testid="co-edit-btn"
            >
              <PenLine size={14} /> {c.edit_btn}
            </button>
          )}
          {isDraft && !editing && (
            <button
              onClick={approveFlow.stage}
              disabled={approveFlow.staging}
              data-testid="co-approve-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              {approveFlow.staging ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />} {c.approve_btn}
            </button>
          )}
          {isDraft && !editing && (
            <button
              onClick={cancelFlow.stage}
              disabled={cancelFlow.staging}
              data-testid="co-cancel-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] text-rose-700"
            >
              <XCircle size={14} /> {c.cancel_btn}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {isImmutable && (
          <div className="jc-card p-4 border-l-4 border-slate-400 text-sm text-[var(--jc-text-soft)]" data-testid="co-immutable-banner">
            {c.edit_locked}
          </div>
        )}
        <div className="text-xs text-[var(--jc-text-soft)]" data-testid="co-approval-note">
          {c.contractor_approval_note}
        </div>

        {editing ? (
          <EditPanel co={co} onSaved={(x) => { setCo(x); setEditing(false); toast.success(c.approve_success ? "" : ""); }} onCancel={() => setEditing(false)} />
        ) : (
          <ScopeSection co={co} />
        )}
        <FinancialSection co={co} />
        <ScheduleSection co={co} />
        <HistorySection co={co} />

        {/* Approve confirmation dialog (inline) */}
        {approveFlow.pending && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
            data-testid="co-approve-confirm-dialog"
          >
            <div className="bg-white rounded-md p-6 max-w-md w-full mx-4">
              <div className="font-display text-xl mb-2">{c.approve_confirm_title}</div>
              <p className="text-sm text-[var(--jc-text-soft)] mb-4">{c.approve_confirm_body}</p>
              <div className="flex justify-end gap-2">
                <button onClick={approveFlow.dismiss} className="px-3 py-2 text-sm border border-[var(--jc-border-strong)] rounded-md" data-testid="co-approve-dismiss">{c.approve_confirm_cancel}</button>
                <button onClick={approveFlow.confirm} className="px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary" data-testid="co-approve-confirm-btn" disabled={approveFlow.staging}>
                  {approveFlow.staging ? <Loader2 className="animate-spin" size={14} /> : c.approve_confirm_yes}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel confirmation dialog (inline) */}
        {cancelFlow.pending && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
            data-testid="co-cancel-confirm-dialog"
          >
            <div className="bg-white rounded-md p-6 max-w-md w-full mx-4">
              <div className="font-display text-xl mb-2">{c.cancel_confirm_title}</div>
              <textarea
                data-testid="co-cancel-reason"
                value={cancelFlow.reason}
                onChange={(e) => cancelFlow.setReason(e.target.value)}
                placeholder={c.cancel_reason_placeholder}
                rows={2}
                className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm mb-3"
              />
              <div className="flex justify-end gap-2">
                <button onClick={cancelFlow.dismiss} className="px-3 py-2 text-sm border border-[var(--jc-border-strong)] rounded-md" data-testid="co-cancel-dismiss">{c.cancel_confirm_no}</button>
                <button onClick={cancelFlow.confirm} className="px-4 py-2 text-sm font-semibold rounded-md bg-rose-600 text-white" data-testid="co-cancel-confirm-btn" disabled={cancelFlow.staging}>
                  {cancelFlow.staging ? <Loader2 className="animate-spin" size={14} /> : c.cancel_confirm_yes}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------- PREVIEW

export const ChangeOrderPreview = () => {
  const { pid, coid } = useParams();
  const navigate = useNavigate();
  const { lang, c } = useCopy();
  const [d, setD] = useState(null);

  useLeviWorkspaceHint("change_order_preview", {
    active_project_id: pid,
    open_document_type: "change_order",
    open_document_id: coid,
    current_section: "change_order_preview",
  });

  useEffect(() => {
    api.get(`/change-orders/${coid}/preview`).then((r) => setD(r.data));
  }, [coid]);

  if (!d) return <DashboardLayout title={c.printable_preview_btn} subtitle="…"><div /></DashboardLayout>;

  const co = d.change_order;
  const meta = d.preview_meta;
  const proj = d.project;
  const ctr = d.source_contract_ref;
  const brand = d.contractor_branding;
  const docLang = co.language === "es" ? "es" : "en";
  const dc = COPY[docLang];

  const summary = docLang === "es"
    ? (co.scope_delta?.scope_summary_es || co.scope_delta?.scope_summary_en)
    : (co.scope_delta?.scope_summary_en || co.scope_delta?.scope_summary_es);

  return (
    <DashboardLayout
      title={`${dc.preview_kind_label} #${co.change_order_number}`}
      subtitle={<StatusPill status={co.status} />}
      actions={
        <div className="flex items-center gap-2 print:hidden">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]" data-testid="co-preview-back">
            <ArrowLeft size={14} /> {dc.preview_back}
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]" data-testid="co-preview-print">
            <Printer size={14} /> {dc.preview_print}
          </button>
        </div>
      }
    >
      <div
        data-testid="co-preview-sheet"
        className="max-w-4xl mx-auto bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-10 print:shadow-none print:border-0 relative"
      >
        {meta.is_draft && (
          <div
            data-testid="co-preview-draft-watermark"
            className="absolute top-6 right-8 text-[10px] tracking-widest px-3 py-1 rounded-full bg-amber-100 text-amber-900 font-semibold uppercase"
          >
            {dc.preview_draft_watermark}
          </div>
        )}
        <header className="flex items-start justify-between border-b border-[var(--jc-border)] pb-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt="logo" className="h-8" data-testid="co-preview-logo" />
              ) : (
                <Mark size={32} />
              )}
              <div className="font-display text-lg" data-testid="co-preview-company">{brand.company_name || (docLang === "es" ? "Su empresa" : "Your Company")}</div>
            </div>
            <div className="overline">{dc.preview_kind_label}</div>
            <h1 className="font-display text-3xl mt-1" data-testid="co-preview-number">
              #{co.change_order_number}
            </h1>
            <div className="text-sm text-[var(--jc-text-soft)] mt-1">
              <span className="font-semibold">{dc.preview_customer}:</span> {proj.customer_name || "—"}
            </div>
            <div className="text-sm text-[var(--jc-text-soft)]">
              <span className="font-semibold">{dc.preview_project}:</span> {proj.job_type || "—"}{proj.site_address ? ` · ${proj.site_address}` : ""}
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="text-[var(--jc-text-soft)]">{dc.preview_source_contract}</div>
            <div className="font-semibold" data-testid="co-preview-source-contract">
              {ctr.contract_number || ctr.id.substring(0, 12)}
            </div>
            <div className="text-xs text-[var(--jc-text-soft)] mt-1" data-testid="co-preview-signed-hash">
              {dc.preview_signed_hash}:<br />
              <span className="font-mono-jc">{(ctr.signed_content_hash || "").substring(0, 16)}…</span>
            </div>
            <div className="text-xs text-[var(--jc-text-soft)] mt-2">
              {formatDate(co.created_at, docLang)}
            </div>
          </div>
        </header>

        <section className="mt-6" data-testid="co-preview-scope">
          <div className="overline mb-2">{dc.preview_scope_summary}</div>
          {summary ? <p className="text-sm">{summary}</p> : <p className="text-sm text-[var(--jc-text-soft)]">{dc.preview_no_scope}</p>}
        </section>

        <section className="mt-6"><ScopeSection co={co} forceLang={docLang} /></section>
        <section className="mt-6"><FinancialSection co={co} forceLang={docLang} /></section>
        <section className="mt-6"><ScheduleSection co={co} forceLang={docLang} /></section>

        <footer className="mt-8 pt-4 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]">
          <div data-testid="co-preview-branding-line">
            {brand.company_name}{brand.dba ? ` (${brand.dba})` : ""}{brand.phone ? ` · ${brand.phone}` : ""}{brand.email ? ` · ${brand.email}` : ""}{brand.license_number ? ` · Lic. ${brand.license_number}` : ""}
          </div>
          <div className="mt-2">{dc.contractor_approval_note}</div>
          <div className="mt-1" data-testid="co-preview-upf-note">{dc.upf_note}</div>
        </footer>
      </div>
    </DashboardLayout>
  );
};

export default ChangeOrdersList;
